import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ColorMode, useColorMode } from '@chakra-ui/react';
import { ThemeVariant, themes } from '@/theme';

interface ColorSchemes {
  primary: string;
  secondary: string;
  success: string;
  error: string;
  warning: string;
  neutral: string;
  info: string;
}

interface ThemeContextType {
  themeVariant: ThemeVariant;
  colorMode: ColorMode;
  setThemeVariant: (variant: ThemeVariant) => void;
  toggleColorMode: () => void;
  colorSchemes: ColorSchemes;
}

const getColorSchemes = (themeVariant: ThemeVariant): ColorSchemes => {
  return {
    primary: 'primary',
    secondary: 'secondary',
    success: 'success',
    error: 'error',
    warning: 'warning',
    neutral: 'secondary',
    info: 'info',
  };
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const [themeVariant, setThemeVariantState] = useState<ThemeVariant>(() => {
    const stored = localStorage.getItem('theme-variant') as ThemeVariant;
    return (stored && themes[stored]) ? stored : 'ocean';
  });

  useEffect(() => {
    const stored = localStorage.getItem('theme-variant') as ThemeVariant;
    if (stored && themes[stored] && stored !== themeVariant) {
      setThemeVariantState(stored);
    }
  }, []);

  const setThemeVariant = (variant: ThemeVariant) => {
    setThemeVariantState(variant);
    localStorage.setItem('theme-variant', variant);
    window.dispatchEvent(new CustomEvent('theme-change', { 
      detail: { variant, colorMode } 
    }));
  };

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme-variant' && e.newValue) {
        const newVariant = e.newValue as ThemeVariant;
        if (themes[newVariant]) {
          setThemeVariantState(newVariant);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value: ThemeContextType = {
    themeVariant,
    colorMode,
    setThemeVariant,
    toggleColorMode,
    colorSchemes: getColorSchemes(themeVariant),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 